import axios from 'axios';

function parseDurationMinutes(iso) {
  const m = /^PT(?:(\d+)H)?(?:(\d+)M)?$/.exec(iso || '');
  if (!m) return undefined;
  return (parseInt(m[1] || '0', 10) * 60) + parseInt(m[2] || '0', 10);
}

function formatDuration(iso) {
  if (!iso) return '';
  const m = /^PT(?:(\d+)H)?(?:(\d+)M)?$/.exec(iso);
  if (!m) return iso;
  const h = m[1] || '0';
  const min = m[2] || '0';
  return `${h}h ${min}m`;
}

// The DB-fallback path (backend/models/Flight.js) stores price as
// {adult,child,infant} and stops as a string enum, unlike the
// Duffel/Amadeus-transformed shape (number price, numeric stops).
function normalizePrice(price) {
  if (price && typeof price === 'object') return price.adult;
  return price;
}

function normalizeStops(stops) {
  if (typeof stops === 'string') {
    if (stops === 'Non-stop') return 0;
    if (stops === '1 stop') return 1;
    if (stops === '2+ stops') return 2;
    return 0;
  }
  return stops ?? 0;
}

/* Adapts flyairlinebooking's nested backend flight shape
   ({from:{code,city}, departure:{time}, ...}) into the flat shape
   FlightCard/BookingPage/ConfirmationPage expect ({origin, departureTime, ...}). */
export function adaptFlight(f) {
  const flat = {
    id: f.id || f._id,
    airline: f.airlineName || f.airline,
    airlineCode: f.airline,
    flightNumber: f.flightNumber,
    origin: f.from?.code,
    originCity: f.from?.city,
    destination: f.to?.code,
    destinationCity: f.to?.city,
    departureTime: f.departure?.time,
    arrivalTime: f.arrival?.time,
    duration: formatDuration(f.duration),
    durationMinutes: parseDurationMinutes(f.duration),
    stops: normalizeStops(f.stops),
    price: normalizePrice(f.price),
    totalPrice: normalizePrice(f.price),
    date: f.departure?.date,
    baggage: f.amenities?.meals ? '1 carry-on + meal' : '1 carry-on',
    seats: f.seatsAvailable,
  };

  // Return-leg data from the backend has no origin/destination of its own
  // (it's implicitly the reverse of the outbound leg) and doesn't capture a
  // separate marketing carrier, so we approximate both from the outbound leg.
  if (f.returnFlight) {
    flat.returnFlight = {
      flightNumber: f.returnFlight.flightNumber,
      airlineCode: f.airline,
      airline: f.airlineName || f.airline,
      origin: f.to?.code,
      originCity: f.to?.city,
      destination: f.from?.code,
      destinationCity: f.from?.city,
      departureTime: f.returnFlight.departure?.time,
      arrivalTime: f.returnFlight.arrival?.time,
      duration: formatDuration(f.returnFlight.duration),
      stops: f.returnFlight.stops ?? 0,
      date: f.returnFlight.departure?.date,
    };
  }

  return flat;
}

export function searchFlights({ origin, destination, date, returnDate, passengers, adults, children, infants, travelClass }) {
  return axios.get('/api/flights', {
    params: {
      from: origin,
      to: destination,
      date,
      passengers,
      adults,
      children,
      infants,
      travelClass,
      tripType: returnDate ? 'roundtrip' : 'oneway',
      returnDate: returnDate || undefined,
    }
  }).then(res => ({
    flights: (res.data.data || []).map(adaptFlight).map((f, i) => {
      // Duffel/Amadeus prices are the total for the whole party
      const raw = res.data.data[i];
      const count = parseInt(passengers) || 1;
      if (typeof raw.price !== 'number' || count <= 1) return f;
      return { ...f, totalPrice: raw.price, price: Math.round((raw.price / count) * 100) / 100 };
    }),
    source: (res.data.sources || [])[0] || '',
  }));
}
